import { Preferences } from '@capacitor/preferences'

import Schema from '@krknet/bpson'
import LZString from 'lz-string'
import mitt from 'mitt'

const settings = {
  autoSync: false,
  syncRate: 15000,
  maxBadgeSize: 30
}

export default class BaseStore {
  #emitter = mitt()
  #isSyncing = false
  #unsent = 0
  #lock = new Lock()
  #isBlocked = false

  static get storageKey () { return 'tracker-base' }
  static get apiFunction () { return null }
  static get schema () { return new Schema([this.rawSchema]) }

  get isSyncing () { return this.#isSyncing }
  get unsent () { return this.#unsent }

  constructor (api, options = {}) {
    this.api = api
    this.options = {
      ...settings,
      ...options
    }
    this.activeCampaign = null

    if (this.options.autoSync) this.activateAutosync()
  }

  block () { this.#isBlocked = true }
  unblock () { this.#isBlocked = false }
  setBlocked (value) { this.#isBlocked = Boolean(value) }
  get isBlocked () { return this.#isBlocked }

  on (event, callback) { this.#emitter.on(event, callback) }
  off (event, callback) { this.#emitter.off(event, callback) }
  _emit (event, data) { this.#emitter.emit(event, data) }

  setActiveCampaign (campaignID) { this.activeCampaign = campaignID }

  setOptions (newOptions = {}) {
    for (const [key, value] of Object.entries(newOptions)) {
      if (value === undefined) continue

      switch (key) {
        case 'syncRate':
          if (Number(newOptions.syncRate) !== this.options.syncRate) {
            const restart = this.interval !== undefined
            this.deactivateAutosync()
            this.options.syncRate = Number(newOptions.syncRate)
            if (restart) this.activateAutosync()
          }
          break
        default:
          this.options[key] = value
      }
    }

    return this
  }

  async activateAutosync () {
    if (this.interval) clearInterval(this.interval)
    this.interval = setInterval(() => { this.sync() }, this.options.syncRate)
    this.sync()
  }

  deactivateAutosync () {
    if (this.interval) clearInterval(this.interval)
    this.interval = undefined
  }

  async push (entry) {
    if (!entry) return
    if (this.isBlocked) return
    const deflated = this.constructor.deflate ? this.constructor.deflate(entry) : entry
    return this.append([deflated])
  }

  async sync () {
    if (this.#isSyncing) return

    await this.#lock.acquire()
    // console.log('BaseStore.sync', 'start')
    const backlog = (await this.#get()).sort((a, b) => a.recorded_datetime - b.recorded_datetime)
    const toSync = backlog.slice(0, this.options.maxBadgeSize)
    // console.log('BaseStore.sync reading', backlog.length, toSync.length)
    if (toSync.length === 0) return this.#lock.release()
    this.#isSyncing = true

    const newUnsent = backlog.slice(this.options.maxBadgeSize)
    await this.#set(newUnsent)
    await this.#lock.release()

    const payload = this.constructor.compress(toSync)
    // console.log('BaseStore.sync sending', toSync.length)

    return this.api[this.constructor.apiFunction](payload, { activeCampaign: this.activeCampaign, deviceID: this.options.deviceID })
      .then(res => { this._emit('sync', res.data) })
      .catch(error => {
        console.log(error)
        return this.append(toSync)
      })
      .then(() => {
        this.#isSyncing = false
        // console.log('BaseStore.sync done')
      })
  }

  async append (entries = []) {
    if (entries.length === 0) return

    await this.#lock.acquire()
    // console.log('BaseStore.append')
    const oldEntries = await this.#get()
    const newEntries = oldEntries.concat(entries)
    await this.#set(newEntries)
    await this.#lock.release()
  }

  async set (entries = []) {
    await this.#lock.acquire()
    await this.#set(entries)
    await this.#lock.release()
  }

  async #set (entries) {
    this.#unsent = entries.length
    await Preferences.set({ key: this.constructor.storageKey, value: JSON.stringify(entries) })
    // console.log('< BaseStore.#set', entries.length)
  }

  async get () {
    await this.#lock.acquire()
    const entries = await this.#get()
    await this.#lock.release()
    return entries
  }

  async #get () {
    const entries = await Preferences.get({ key: this.constructor.storageKey }).then(d => d.value ? JSON.parse(d.value) : [])
    // console.log('> BaseStore.#get', entries.length)
    return entries
  }

  async clear () {
    await this.#lock.acquire()
    this.#unsent = 0
    await Preferences.remove({ key: this.constructor.storageKey })
    await this.#lock.release()
  }

  static compress (entries) {
    return LZString.compressToUint8Array(this.schema.encode(entries).toString('base64'))
  }
}

class Lock {
  #isAcquired = false
  #queue = []

  async acquire () {
    if (this.#isAcquired) return new Promise(resolve => this.#queue.push(resolve))
    // if (this.#isAcquired) return new Promise(resolve => this.#queue.push(() => { console.log('Lock.acquired', 'queued'); resolve() }))
    this.#isAcquired = true
    // console.log('Lock.acquired')
  }

  release () {
    // console.log('Lock.released')
    if (!this.#queue.length && this.#isAcquired) {
      this.#isAcquired = false
      return
    }

    const next = this.#queue.shift()
    next && next()
  }
}
